/**
 * Reconciliation Session Page (UI-10)
 *
 * Xero-style reconciliation wizard on BE-13:
 * - Statement date + balance inputs (start), then the session at its own URL
 * - Running total card (the server's figures after every tick)
 * - Ledger lines up to the statement date (older unticked ones too) with checkboxes
 * - Add Adjustment dialog (TC admin), Cancel while nothing is adjusted
 * - Complete when difference = 0; a completed session opens read-only
 * Routes: /accounting/reconciliation/new, /accounting/reconciliation/:id
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, Card, CardContent, TextField,
  MenuItem, Checkbox, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider,
} from '@mui/material';
import { CheckCircle, ArrowBack, Add, SelectAll, Deselect, Close } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { CurrencyText } from '../../components/accounting';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';
import { reconciliationService } from '../../services/accounting/reconciliation.service';

const isAdjustmentLine = (t) => Boolean(t.isAdjustment ?? t.adjustment);
const accountKey = (a) => `${a.accountType}:${a.accountId}`;

const ReconciliationSession = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const passedAccount = location.state?.account || null;
  const isAdmin = currentUser?.role === 'TC_ADMIN';

  // Account selection (start)
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountKey, setSelectedAccountKey] = useState(passedAccount ? accountKey(passedAccount) : '');

  // Session state
  const [session, setSession] = useState(null);
  const [statementDate, setStatementDate] = useState(toYmd(new Date()));
  const [statementBalance, setStatementBalance] = useState('');
  const [matchedIds, setMatchedIds] = useState(new Set());

  // Adjustment dialog
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [adjAccountId, setAdjAccountId] = useState('');
  const [adjError, setAdjError] = useState(null);
  const [adjSaving, setAdjSaving] = useState(false);
  const [glAccounts, setGlAccounts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const saveTimer = useRef(null);

  const applySession = useCallback((data) => {
    setSession(data);
    setMatchedIds(new Set((data?.items || []).filter((t) => t.selected).map((t) => t.id)));
  }, []);

  // Start form: the accounts; a session URL: the session itself
  useEffect(() => {
    if (isNew) {
      reconciliationService.getAccountStatus()
        .then((res) => setAccounts(res?.data || []))
        .catch((err) => setError(serverMessage(err, 'Failed to load accounts')));
      return;
    }
    setLoading(true);
    reconciliationService.getReconciliation(id)
      .then((res) => applySession(res?.data))
      .catch((err) => setError(serverMessage(err, 'Failed to load the reconciliation')))
      .finally(() => setLoading(false));
  }, [isNew, id, applySession]);

  useEffect(() => {
    if (isAdmin) reconciliationService.getGlAccounts().then((res) => setGlAccounts(res?.data || [])).catch(() => {});
  }, [isAdmin]);

  const selectedAccount = accounts.find((a) => accountKey(a) === selectedAccountKey) || passedAccount;

  const handleStart = async () => {
    if (!selectedAccount || !statementBalance) return;
    setLoading(true);
    setError(null);
    try {
      const res = await reconciliationService.startReconciliation({
        accountType: selectedAccount.accountType,
        accountId: selectedAccount.accountId,
        statementDate,
        statementBalance,
      });
      navigate(`/accounting/reconciliation/${res.data.id}`, { replace: true });
    } catch (err) {
      setError(serverMessage(err, 'Failed to start reconciliation'));
    } finally {
      setLoading(false);
    }
  };

  const started = Boolean(session);
  const inProgress = session?.status === 'IN_PROGRESS';
  const transactions = session?.items || [];
  const adjustments = session?.adjustments || [];
  const difference = Number(session?.difference || 0);
  const isDifferenceZero = Math.abs(difference) < 0.005;
  const adjustmentTotal = Number(session?.adjustmentTotal || 0);

  // Ticks save straight away: the full list replaces the selection and the server returns the new totals
  const saveTicks = (next) => {
    setMatchedIds(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const res = await reconciliationService.matchItems(session.id, Array.from(next));
        applySession(res?.data);
      } catch (err) {
        setError(serverMessage(err, 'The ticks were not saved'));
        reconciliationService.getReconciliation(session.id).then((res) => applySession(res?.data)).catch(() => {});
      } finally {
        setSaving(false);
      }
    }, 250);
  };
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const toggleMatch = (txn) => {
    if (!inProgress || isAdjustmentLine(txn)) return;
    const next = new Set(matchedIds);
    if (next.has(txn.id)) next.delete(txn.id);
    else next.add(txn.id);
    saveTicks(next);
  };

  const selectAll = () => saveTicks(new Set(transactions.map((t) => t.id)));
  const clearAll = () => saveTicks(new Set(transactions.filter(isAdjustmentLine).map((t) => t.id)));

  const openAdjustment = () => {
    setAdjAmount(String(difference.toFixed(2)));
    setAdjReason('');
    setAdjAccountId('');
    setAdjError(null);
    setAdjOpen(true);
  };

  const handleAddAdjustment = async () => {
    if (!adjAmount || !adjReason.trim()) return;
    setAdjSaving(true);
    setAdjError(null);
    try {
      const res = await reconciliationService.addAdjustment(session.id, {
        amount: adjAmount,
        reason: adjReason.trim(),
        counterAccountId: adjAccountId || undefined,
      });
      applySession(res?.data);
      setAdjOpen(false);
    } catch (err) {
      setAdjError(serverMessage(err, 'Failed to add adjustment'));
    } finally {
      setAdjSaving(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    setError(null);
    try {
      await reconciliationService.completeReconciliation(session.id);
      navigate('/accounting/reconciliation');
    } catch (err) {
      setError(serverMessage(err, 'Failed to complete reconciliation'));
    } finally {
      setCompleting(false);
    }
  };

  const handleCancel = async () => {
    try {
      await reconciliationService.cancelReconciliation(session.id);
      navigate('/accounting/reconciliation');
    } catch (err) {
      setCancelOpen(false);
      setError(serverMessage(err, 'Failed to cancel the reconciliation'));
    }
  };

  const title = started
    ? `${inProgress ? 'Reconcile' : 'Reconciliation'}: ${session.accountName}`
    : 'New Reconciliation';

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/accounting/reconciliation')}
          sx={{ textTransform: 'none', fontSize: fs.sm }}
        >
          Back
        </Button>
        <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>{title}</Typography>
      </Box>

      {started && !inProgress && (
        <Alert severity={session.status === 'COMPLETED' ? 'success' : 'info'} sx={{ mb: 1.5 }}>
          {session.status === 'COMPLETED'
            ? `Completed${session.reconciledAt ? ` on ${formatDate(session.reconciledAt)}` : ''} — locked. The lines below were ticked.`
            : 'This reconciliation was cancelled.'}
        </Alert>
      )}

      {/* Account selection + statement inputs */}
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {!started && (
              <TextField
                select
                label="Account"
                value={selectedAccountKey}
                onChange={(e) => setSelectedAccountKey(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              >
                {accounts.map((a) => (
                  <MenuItem key={accountKey(a)} value={accountKey(a)} disabled={Boolean(a.openReconciliationId)}>
                    {a.accountName}{a.openReconciliationId ? ' — in progress' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Statement Date"
              type="date"
              value={started ? session.statementDate : statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toYmd(new Date()) }}
              disabled={started}
            />
            <TextField
              label="Statement Balance (AED)"
              type="number"
              value={started ? session.statementBalance : statementBalance}
              onChange={(e) => setStatementBalance(e.target.value)}
              size="small"
              inputProps={{ step: '0.01' }}
              disabled={started}
            />
            {!started && (
              <Button
                variant="contained"
                size="small"
                onClick={handleStart}
                disabled={loading || !selectedAccount || statementBalance === ''}
                sx={{ textTransform: 'none', fontSize: fs.sm }}
              >
                {loading ? 'Loading...' : 'Start'}
              </Button>
            )}
            {!started && selectedAccount?.lastReconciled && (
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
                Last reconciled up to {formatDate(selectedAccount.lastReconciled)}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {!isNew && loading && !session && <Typography sx={{ fontSize: fs.sm, color: 'text.secondary' }}>Loading…</Typography>}

      {/* Session content */}
      {started && (
        <>
          {/* Running Total Card */}
          <Card
            variant="outlined"
            sx={{
              mb: 1.5,
              borderColor: isDifferenceZero ? 'success.main' : 'divider',
              borderWidth: isDifferenceZero ? 2 : 1,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 1 }}>
                Running Total {saving && <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary', ml: 1 }}>saving…</Typography>}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Row label="Opening Cleared" value={session.openingClearedBalance} />
                <Row label="+ Selected" value={session.selectedTotal} />
                {adjustmentTotal !== 0 && (
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', textAlign: 'right' }}>
                    includes adjustments of {formatCurrency(adjustmentTotal)}
                  </Typography>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Row label="= Cleared Balance" value={session.calculatedBalance} bold />
                <Row label="Statement Balance" value={session.statementBalance} />
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Difference</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{
                      fontSize: fs.sm,
                      fontWeight: fw.bold,
                      color: isDifferenceZero ? 'success.main' : 'error.main',
                    }}>
                      {formatCurrency(difference)}
                    </Typography>
                    {isDifferenceZero && <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />}
                  </Box>
                </Box>
                <Row label={`Outstanding (${session.outstandingCount || 0} line${session.outstandingCount === 1 ? '' : 's'})`} value={session.outstandingTotal} muted />
              </Box>
            </CardContent>
          </Card>

          {/* Outstanding cheques (bank) — information only */}
          {(session.outstandingCheques || []).length > 0 && (
            <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 0.5 }}>
                Cheques issued, not yet cleared: {formatCurrency(session.outstandingChequesTotal)}
              </Typography>
              {session.outstandingCheques.map((c) => (
                <Typography key={c.id} sx={{ fontSize: fs.xs }}>
                  {c.chequeNumber} · {formatDate(c.chequeDate)} · {formatCurrency(c.amount)}
                </Typography>
              ))}
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 0.5 }}>
                For information — they are not part of the difference.
              </Typography>
            </Alert>
          )}

          {/* Transactions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>
              {inProgress ? `Unmatched Transactions (${transactions.length})` : `Ticked Transactions (${transactions.length})`}
            </Typography>
            {inProgress && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<SelectAll />} onClick={selectAll} sx={{ textTransform: 'none', fontSize: fs.xs }}>
                  Select All
                </Button>
                <Button size="small" startIcon={<Deselect />} onClick={clearAll} sx={{ textTransform: 'none', fontSize: fs.xs }}>
                  Clear All
                </Button>
              </Box>
            )}
          </Box>

          <TableContainer sx={{ mb: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {inProgress && <TableCell padding="checkbox" sx={{ width: 40 }} />}
                  <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>Date</TableCell>
                  <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>Description</TableCell>
                  <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={inProgress ? 5 : 4} sx={{ fontSize: fs.sm, color: 'text.secondary', textAlign: 'center' }}>
                      No transactions on this account up to the statement date.
                    </TableCell>
                  </TableRow>
                )}
                {transactions.map((txn) => {
                  const adjustment = isAdjustmentLine(txn);
                  const isMatched = matchedIds.has(txn.id) || adjustment;
                  return (
                    <TableRow
                      key={txn.id}
                      hover={inProgress && !adjustment}
                      onClick={() => toggleMatch(txn)}
                      sx={{ cursor: inProgress && !adjustment ? 'pointer' : 'default', bgcolor: isMatched && inProgress ? 'action.selected' : 'inherit' }}
                    >
                      {inProgress && (
                        <TableCell padding="checkbox">
                          <Checkbox checked={isMatched} disabled={adjustment} size="small" />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{formatDate(txn.date)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>
                        {txn.description}
                        {txn.reference && (
                          <Typography component="div" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{txn.reference}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', color: parseFloat(txn.amount) < 0 ? 'error.main' : 'success.main' }} align="right">
                        <CurrencyText amount={txn.amount} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: fs.xs, color: isMatched ? 'success.main' : 'text.secondary' }}>
                          {adjustment ? 'adjustment' : isMatched ? 'matched' : 'outstanding'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Adjustments list */}
          {adjustments.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 0.5 }}>Adjustments</Typography>
              {adjustments.map((adj) => (
                <Box key={adj.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3, gap: 2 }}>
                  <Typography sx={{ fontSize: fs.sm }}>
                    {adj.reason}{adj.counterAccountName ? ` (${adj.counterAccountNumber} ${adj.counterAccountName})` : ''}
                  </Typography>
                  <Typography sx={{ fontSize: fs.sm, whiteSpace: 'nowrap' }}>{formatCurrency(adj.amount)}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Action buttons */}
          {inProgress && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {adjustments.length === 0 && (
                <Button size="small" startIcon={<Close />} onClick={() => setCancelOpen(true)} sx={{ textTransform: 'none', fontSize: fs.sm }}>
                  Cancel Reconciliation
                </Button>
              )}
              {!isDifferenceZero && isAdmin && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={openAdjustment}
                  sx={{ textTransform: 'none', fontSize: fs.sm }}
                >
                  Add Adjustment
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                color="success"
                startIcon={<CheckCircle />}
                onClick={handleComplete}
                disabled={!isDifferenceZero || completing || saving}
                sx={{ textTransform: 'none', fontSize: fs.sm }}
              >
                {completing ? 'Completing...' : 'Complete Reconciliation'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Adjustment Dialog */}
      <Dialog open={adjOpen} onClose={() => setAdjOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Add Adjustment</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {adjError && <Alert severity="error" sx={{ mb: 2 }}>{adjError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Amount (AED)"
              type="number"
              value={adjAmount}
              onChange={(e) => setAdjAmount(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ step: '0.01' }}
              helperText={`Current difference: ${formatCurrency(difference)}. Negative lowers the account, positive raises it.`}
            />
            <TextField
              label="Reason"
              value={adjReason}
              onChange={(e) => setAdjReason(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. Bank charge not recorded"
            />
            <TextField
              select
              label="Counter Account"
              value={adjAccountId}
              onChange={(e) => setAdjAccountId(e.target.value)}
              size="small"
              fullWidth
              helperText="Leave on default: Bank Charges (or Cash Over / Short for a cash drawer) when the account goes down; choose one when it goes up"
            >
              <MenuItem value="">Default</MenuItem>
              {glAccounts.filter((a) => a.manualAllowed && a.active !== false).map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.accountNumber} - {a.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdjOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddAdjustment}
            disabled={!adjAmount || Number(adjAmount) === 0 || !adjReason.trim() || adjSaving}
            sx={{ textTransform: 'none' }}
          >
            {adjSaving ? 'Posting...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Cancel Reconciliation</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fs.sm }}>
            The ticks are dropped and the account can be reconciled again from scratch. Nothing is posted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none' }}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleCancel} sx={{ textTransform: 'none' }}>Cancel Reconciliation</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper component for running total rows
const Row = ({ label, value, bold, muted }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography sx={{ fontSize: fs.sm, fontWeight: bold ? fw.semibold : fw.normal, color: muted ? 'text.secondary' : 'inherit' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: fs.sm, fontWeight: bold ? fw.semibold : fw.normal, color: muted ? 'text.secondary' : 'inherit' }}>
      {formatCurrency(value)}
    </Typography>
  </Box>
);

export default ReconciliationSession;
